import * as React from 'react';
import strings from 'strings/strings.json';
import useNeveForm from 'components/forms/NeveForm';
import useUploadRequest from 'hooks/useUploadRequest';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import useModifyRequest from 'hooks/useModifyRequest';
import { logError } from 'lib/debug-helpers';
import { Activity, ActivityType, TaskContent } from 'models/Activity';
import GeneratedContentSection from './shared/GeneratedContentSection';
import { Course } from 'models/Course';
import { TaskContentField } from 'models/inputModels/CreateTaskRequest';
import ResourcesList from '../../ResourcesList';
import DeleteActivityAlert from './shared/DeleteActivityAlert';
import { EditTaskFormProps, ModuleOnlyHasOneTask } from './shared/SharedEditTaskComponents';
import {
    ActivityFormState,
    ActivityDetailsInput,
    ResourcesInput,
    TextImageTaskInput,
    ModalState,
} from '../shared/SharedActivityFormComponents';
import { EditActivityFormCard } from '../shared/EditActivityFormCard';
import BaseDetailsForm from '../shared/DetailsForm';
import { HorizontalRule } from 'components/Elements';
import { FieldErrors, useFieldArray, UseFormRegister } from 'react-hook-form';
import { Modals, TextAndImages } from '../shared/SharedTextAndImageTaskForm';

interface EditTextImageTaskFields extends ActivityDetailsInput {
    durationInMins: number;
    resources: ResourcesInput[];
    contents: TaskContentField[];
}

export default function EditTextImageTaskForm({
    task: taskProp,
    returnToCourseStructure,
}: EditTaskFormProps): JSX.Element {
    const course = React.useContext(BuildCourseContext).course!;
    const { setCourseState, editMode, refetchCourse, setEditActivityId } = React.useContext(BuildCourseContext);

    const { upload: uploadImage, uploading: uploadingImage } = useUploadRequest();
    const { modifyData: updateTask, loading: savingTask } = useModifyRequest<EditTextImageTaskFields, boolean>(
        `activities/task/textimage/${taskProp.id}`,
        'PUT',
    );

    const [modalState, setModalState] = React.useState<ModalState>('closed');
    const [contentRequiredError, setContentRequiredError] = React.useState(false);
    const [error, setError] = React.useState('');

    const [task, setTask] = React.useState<Activity>(taskProp);
    const [state, setState] = React.useState<ActivityFormState>('clean');

    const addLinkButtonRef = React.useRef<HTMLButtonElement>(null);
    const addTextButtonRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        return () => {
            setEditActivityId('');
        };
    }, []); //eslint-disable-line

    const {
        register,
        handleSubmit,
        control,
        formState: { errors, isDirty },
        watch,
        reset,
    } = useNeveForm<TextImageTaskInput>({
        title: task.title ?? '',
        description: task.description ?? '',
        durationInMins: task.durationInMins ?? 0,
        resources: task.resources.map((x) => ({ title: x.title, url: x.url })),
        contents: task.taskContents.map((c) => {
            if (c.type === 'text') {
                return {
                    type: 'editText',
                    id: c.id,
                    text: c.text,
                };
            } else {
                return {
                    type: 'editImage',
                    id: c.id,
                    filename: c.image.filename,
                    s3Filepath: c.image.s3Filepath,
                    imageDescription: c.imageDescription,
                };
            }
        }),
    });

    const {
        fields: resourceFields,
        append: appendResource,
        remove: removeResource,
    } = useFieldArray({
        control,
        name: 'resources',
    });
    const {
        fields: contentFields,
        append: appendContent,
        remove: removeContent,
    } = useFieldArray({
        control,
        name: 'contents',
    });

    const addResource = (newResource: ResourcesInput) => {
        appendResource(newResource);
        addLinkButtonRef.current?.focus();
    };
    const deleteResource = (index: number) => {
        removeResource(index);
        addLinkButtonRef.current?.focus();
    };
    const closeResourcesModal = () => {
        setModalState('closed');
        addLinkButtonRef.current?.focus();
    };

    async function saveTask(formData: TextImageTaskInput) {
        setError('');

        if (!isDirty) {
            setState('saved');
            return;
        }
        if (formData.contents.length === 0) {
            setContentRequiredError(true);
            addTextButtonRef.current?.focus();
            return;
        }

        const contents: TaskContentField[] = [];
        for (const content of formData.contents) {
            switch (content.type) {
                case 'addText':
                    contents.push({
                        type: 'addText',
                        text: content.text,
                    });
                    break;
                case 'editText':
                    contents.push({
                        type: 'editText',
                        id: content.id,
                        text: content.text,
                    });
                    break;
                case 'editImage':
                    contents.push({
                        type: 'editImage',
                        id: content.id,
                        imageDescription: content.imageDescription,
                    });
                    break;
                case 'addImage':
                    const { errors, value } = await uploadImage(content.file);
                    if (errors && errors.length) {
                        logError(errors);
                        setError('Error creating task');
                        return;
                    }
                    contents.push({
                        type: 'addImage',
                        fileName: value!.filename,
                        filePath: value!.s3Key,
                        imageDescription: content.imageDescription,
                    });
                    break;
            }
        }

        if (!editMode) {
            const request: EditTextImageTaskFields = {
                title: formData.title,
                description: formData.description,
                durationInMins: formData.durationInMins ?? 0,
                resources: formData.resources,
                contents: contents,
            };
            const { errors } = await updateTask(request);
            if (errors) {
                setError('Error creating task');
                return;
            }
            reset(formData);
            await refetchCourse();
            setState('saved');
            return;
        }

        //UPLOAD IMAGES BEFORE UPDATING STATE
        const imagePaths = new Map<number, string>();
        for (let i = 0; i < formData.contents.length; i++) {
            const content = formData.contents[i];
            if (content.type === 'addImage') {
                const uploadResponse = await uploadImage(content.file);
                if (uploadResponse.errors?.length) {
                    setError('Error creating task');
                    return;
                }
                imagePaths.set(i, uploadResponse.value!.s3Key);
            }
        }

        setCourseState((prev) => {
            const copy = structuredClone(prev) as Course;
            const updatedTask = copy.modules.flatMap((m) => m.activities).find((a) => a.id === task.id);
            if (updatedTask) {
                updatedTask.title = formData.title;
                updatedTask.description = formData.description;
                updatedTask.durationInMins = formData.durationInMins ?? null;
                updatedTask.resources = formData.resources;

                updatedTask.taskContents = formData.contents.map((x, i) => {
                    switch (x.type) {
                        default:
                        case 'editText':
                            const editTextContent: TaskContent = {
                                type: 'text',
                                id: x.id,
                                order: i + 1,
                                text: x.text,
                                audio: { s3Filepath: '' },
                            };
                            return editTextContent;
                        case 'addText':
                            const addTextContent: TaskContent = {
                                type: 'text',
                                id: '',
                                order: i + 1,
                                text: x.text,
                                audio: { s3Filepath: '' },
                            };
                            return addTextContent;
                        case 'editImage':
                            const editImageContent: TaskContent = {
                                type: 'image',
                                id: x.id,
                                order: i + 1,
                                imageDescription: x.imageDescription,
                                image: {
                                    id: '',
                                    filename: x.filename,
                                    s3Filepath: x.s3Filepath,
                                },
                                audio: { s3Filepath: '' },
                            };
                            return editImageContent;
                        case 'addImage':
                            const addImageContent: TaskContent = {
                                type: 'image',
                                id: '',
                                order: i + 1,
                                imageDescription: x.imageDescription,
                                image: {
                                    id: '',
                                    filename: x.file.name,
                                    s3Filepath: imagePaths.get(i)!,
                                },
                                audio: { s3Filepath: '' },
                            };
                            return addImageContent;
                    }
                });
            }
            return copy;
        });

        const updatedTaskBaseline: Activity = {
            ...task,
            title: formData.title,
            description: formData.description,
            durationInMins: formData.durationInMins ?? null,
            resources: formData.resources,
            taskContents: formData.contents.map((x, i) => {
                switch (x.type) {
                    default:
                    case 'editText':
                    case 'addText':
                        return {
                            type: 'text',
                            id: x.type === 'editText' ? x.id : '',
                            order: i + 1,
                            text: x.text,
                            audio: { s3Filepath: '' },
                        };
                    case 'editImage':
                        return {
                            type: 'image',
                            id: x.id,
                            order: i + 1,
                            imageDescription: x.imageDescription,
                            image: {
                                id: '',
                                filename: x.filename,
                                s3Filepath: x.s3Filepath,
                            },
                            audio: { s3Filepath: '' },
                        };
                    case 'addImage':
                        return {
                            type: 'image',
                            id: '',
                            order: i + 1,
                            imageDescription: x.imageDescription,
                            image: {
                                id: '',
                                filename: x.file.name,
                                s3Filepath: imagePaths.get(i)!,
                            },
                            audio: { s3Filepath: '' },
                        };
                }
            }),
        };

        setTask(updatedTaskBaseline);
        reset(formData);
        setState('saved');
        return;
    }

    const moduleOnlyHasOneTask = ModuleOnlyHasOneTask(course, task.moduleId);

    return (
        <>
            <EditActivityFormCard
                title={strings.activityDetailsForm.instructionEdit}
                onSubmit={handleSubmit(saveTask)}
                error={error}
                showLastActivityInModuleWarning={editMode && moduleOnlyHasOneTask}
                showSavedSuccessfully={state === 'saved' && !editMode}
                showSavedSuccessfullyEditMode={state === 'saved' && editMode}
                setDeleteAlertOpen={() => setModalState((prev) => (prev === 'closed' ? 'deleteTask' : 'closed'))}
                disableDeleteTaskButton={uploadingImage || savingTask || (editMode && moduleOnlyHasOneTask)}
                disableSubmitButton={uploadingImage || savingTask || (editMode && !isDirty)}
                submitting={uploadingImage || savingTask}
                activityType={ActivityType.TASK}
                pageOne={
                    <DetailsAndLinks
                        register={register}
                        errors={errors}
                        resources={resourceFields}
                        deleteResource={deleteResource}
                        toggleModalOpen={() => setModalState((prev) => (prev === 'closed' ? 'addResource' : 'closed'))}
                        disabled={uploadingImage || savingTask}
                        addLinkButtonRef={addLinkButtonRef}
                        task={task}
                    />
                }
                pageTwo={
                    <TextAndImages
                        fields={contentFields}
                        errors={errors}
                        register={register}
                        append={appendContent}
                        remove={removeContent}
                        addTextButtonRef={addTextButtonRef}
                        contentRequiredError={contentRequiredError}
                        setContentRequiredError={setContentRequiredError}
                        setOpenImageModal={() => setModalState('addImage')}
                        disable={uploadingImage || savingTask}
                        watch={watch}
                    />
                }
            />
            <Modals
                modalState={modalState}
                closeResourceModal={closeResourcesModal}
                closeImageModal={() => setModalState('closed')}
                addResource={addResource}
                setImage={(file: File, description: string) => {
                    appendContent({
                        type: 'addImage',
                        file: file,
                        imageDescription: description,
                        previewUrl: URL.createObjectURL(file),
                    });
                }}
            />
            {modalState === 'deleteTask' && (
                <DeleteActivityAlert
                    taskId={task.id}
                    moduleId={module.id}
                    returnToCourseStructure={returnToCourseStructure}
                    setAlertOpen={() => setModalState('deleteTask')}
                />
            )}
        </>
    );
}

type DetailsAndLinksProps = {
    register: UseFormRegister<TextImageTaskInput>;
    errors: FieldErrors<TextImageTaskInput>;
    resources: ResourcesInput[];
    deleteResource: (index: number) => void;
    toggleModalOpen: () => void;
    disabled: boolean;
    addLinkButtonRef: React.RefObject<HTMLButtonElement>;
    task: Activity;
};

function DetailsAndLinks({
    register,
    errors,
    resources,
    deleteResource,
    toggleModalOpen,
    disabled,
    addLinkButtonRef,
    task,
}: DetailsAndLinksProps) {
    return (
        <>
            <BaseDetailsForm register={register} errors={errors} autoFocus />
            <GeneratedContentSection task={task} />
            <HorizontalRule />
            <ResourcesList
                resources={resources}
                deleteResource={deleteResource}
                toggleModalOpen={toggleModalOpen}
                disabled={disabled}
                addLinkButtonRef={addLinkButtonRef}
            />
        </>
    );
}
